import {computed, observable} from 'mobx';
import {BaseModuleState} from "../../../infrastructure/models/base-module-state";
import {BaseMobxState} from "../../../infrastructure/mobx/base-mobx-state";
import {AppStore} from "../../../core/app/app-store";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../../core/models/device.view-model";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {NavigationManager} from "../../../core/managers/navigation-manager";
import {GameCenterWidgetConfig} from "../game-center-widget.config";
import {ROUTE_NAMES} from "../../../core/consts";
import type {FullGameDomainModel} from "../../../domain/full-game/models/full-game-domain-model";
import {GameCompetitorDomainModel} from "../../../domain/partial-game/models/game-competitor/game-competitor-domain-model";
import {LINEUPS_CONSTS} from "../../../core/app/consts";
import {NAVIGATION_TABS} from "../../../modules/navigation-tabs-module/navigation-tabs-module.consts";

export class GameCenterWidgetState extends BaseModuleState {
  @observable currentIndex = 0;
  @observable shouldRenderLivePenaltiesPopup: Boolean = false;
  @observable forceHideLivePenaltyPopup: Boolean = false;

  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, GameCenterWidgetConfig];

  @computed get game(): FullGameDomainModel {
    return this.dataSource?.game
  }

  @computed get gameId(): Number {
    return this.game?.id
  }

  get isPopulated(): Boolean {
    return true;
  }

  @computed get shouldRenderHeader(): Boolean {
    return !this.appState.isHoverScreenOpen
  }

  @computed get isWidget(): Boolean {
    return this.appState.settings.isWidget;
  }

  @computed get shouldRenderRouter(): Boolean {
    const validRoutes: Array<String> = [ROUTE_NAMES.game];

    return this.device.isSmallView || this.isWidget || validRoutes.includes(this.currentLinkModel?.name);
  }

  @computed get shouldRenderMatch(): Boolean {
    const shouldRenderMatch: Boolean = this.currentIndex === NAVIGATION_TABS.TAB.INDEX.GAME_CENTER.match

    return shouldRenderMatch;
  }

  @computed get isLineups(): Boolean {
    const isHashRoute: Boolean = this.currentIndex === NAVIGATION_TABS.TAB.INDEX.GAME_CENTER.lineups
    const isLineups: Boolean  = this.game && (this.currentIndex === NAVIGATION_TABS.TAB.INDEX.GAME_CENTER.lineups || isHashRoute)

    return isLineups;
  }

  @computed get shouldRenderBoxScores(): Boolean {
    const sportId: Number = this.game?.sport.id;
    const shouldRenderBoxScores: Boolean  = this.isLineups && LINEUPS_CONSTS.boxScoresSportTypes.includes(sportId);

    return shouldRenderBoxScores;
  }

  @computed get shouldRenderLineups(): Boolean {
    const shouldRenderLineups: Boolean  = this.isLineups && !this.shouldRenderBoxScores;

    return shouldRenderLineups;
  }

  @computed get shouldRenderStats(): Boolean {
    const shouldRenderStats: Boolean  = this.currentIndex === NAVIGATION_TABS.TAB.INDEX.GAME_CENTER.stats

    return shouldRenderStats;
  }

  @computed get shouldRenderH2h(): Boolean {
    const shouldRenderH2h: Boolean  = this.currentIndex === NAVIGATION_TABS.TAB.INDEX.GAME_CENTER.h2h

    return shouldRenderH2h;
  }

  @computed get shouldRenderNews(): Boolean {
    const shouldRenderNews: Boolean  = this.currentIndex === NAVIGATION_TABS.TAB.INDEX.GAME_CENTER.news

    return shouldRenderNews;
  }

  @computed get shouldRenderPointByPoint(): Boolean {
    const shouldRenderPointByPoint: Boolean = this.currentIndex === NAVIGATION_TABS.TAB.INDEX.GAME_CENTER.pointByPoint

    return shouldRenderPointByPoint;
  }

  @computed get shouldRenderTrends(): Boolean {
    const hasGameId = this.props.gameId || this.game?.id
    const shouldRenderTrends = this.currentIndex === NAVIGATION_TABS.TAB.INDEX.GAME_CENTER.trends

    return hasGameId && shouldRenderTrends;
  }

  @computed get shouldRenderOdds(): Boolean {
    const hasGameId = this.props.gameId || this.game?.id
    const shouldRenderOdds = this.currentIndex === NAVIGATION_TABS.TAB.INDEX.GAME_CENTER.odds

    return hasGameId && shouldRenderOdds;
  }

  @computed get shouldRenderPropositionBets(): Boolean {
    const hasGameId = this.props.gameId || this.game?.id
    const shouldRenderPropositionBets = this.currentIndex === NAVIGATION_TABS.TAB.INDEX.GAME_CENTER.propositionBets

    return hasGameId && shouldRenderPropositionBets;
  }

  @computed get shouldShowLivePenaltiesPopup(): Boolean {
    return !this.forceHideLivePenaltyPopup && this.shouldRenderLivePenaltiesPopup && this.appSettings.allowPremiumData && !this.isWidget;
  }

  @computed get livePenaltiesAthletes(): Array<Number>{
    return this.game?.eventChartPenaltiesDetails?.athleteIds;
  }

  @computed get livePenaltiesCompetitorNum(): GameCompetitorDomainModel {
    const isHomeCompetitor: Boolean = this.game?.eventChartPenaltiesDetails?.isHomeCompetitor;
    if (isHomeCompetitor) {
      return this.game?.homeCompetitor;
    }
    else{
      return this.game?.awayCompetitor;
    }
  }
  @computed get livePenaltiesUniqueHash(): String {
    return this.game?.eventChartPenaltiesDetails?.uniqueHashEvent;
  }

  @computed get userCountryId() {
    return this.appState.locale?.userCountryId;
  }

  @computed get shouldRenderDBA(): Boolean {
    return !this.device.isSmallView;
  }

  @computed get hasShotChart(): Boolean {
    return this.game?.hasShotChart ||  this.game?.chartEvents?.events?.length > 0;
  }

  @computed get shouldRenderBookmakerReview(): Boolean {
    return !this.isWidget && !this.shouldRenderMatch && !this.shouldRenderNews;
  }

  @computed get shouldRenderExternalAd(): Boolean {
    const shouldRenderExternalAd: Boolean = this.appState?.externalAdsModel?.shouldRender;

    return shouldRenderExternalAd
  }

  @computed get shouldRenderDBAIframe(): Boolean {
    const isUserCountryIdValid: Boolean = this.appState.locale.userCountryId === 21;
    const shouldRenderDBAIframe: Boolean = !this.appState.settings.isWidget && isUserCountryIdValid;

    return shouldRenderDBAIframe;
  }
  @computed get shouldRenderExternalAdOverDBA(): Boolean {
    const shouldRenderDBA = this.shouldRenderDBAIframe && !this.appState.externalAdsModel.remoteConfigModel.adsPrioritize;
    const shouldRenderExternalAd = this.shouldRenderExternalAd && !shouldRenderDBA;
    return shouldRenderExternalAd;
  }

  @computed get showOdds(): Boolean {
    return this.appState.showOdds;
  }
}
